import { Box, Button, InputBase, MenuItem, Select, Typography } from "@mui/material";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import { useEffect, useState } from "react";
import { editProject, getProject } from "../../services/projectService";
import { getInspModel, getSimpleInspModelList } from "../../services/modelService";
import { IEditProject, IResponseProjectData, InspModelItem } from "@types";
import { t } from "i18next";
import { imageFlat } from "../../utils/utils";
import { useSetRecoilState } from "recoil";
import { projectInfoState, serverFileState } from "../../recoil";
import { inspModelUuidState, myAIModuleState } from "../../recoil/upgrade";
import { toast } from "react-toastify";
import { removeImportedModules } from "../../services/aiServer";

interface ProjectContentProps {
  id?: string;
  setProjectData: React.Dispatch<React.SetStateAction<IResponseProjectData>>;
}

type Form = { project_name: string; material_name: string; inspModel_id: number | null };
const emptyForm: Form = { project_name: "", material_name: "", inspModel_id: null };

// macOS System Settings look
const ACCENT = "#007AFF";
const FONT = '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Apple SD Gothic Neo", "Helvetica Neue", sans-serif';

const fieldSx = {
  width: 280,
  fontSize: 13,
  fontFamily: FONT,
  px: 1,
  py: 0.25,
  borderRadius: "6px",
  transition: "box-shadow 0.15s, background-color 0.15s",
  "& input": { textAlign: "right", p: "4px 0" },
  "&:hover": { bgcolor: "rgba(0,0,0,0.03)" },
  "&.Mui-focused": { bgcolor: "background.paper", boxShadow: `0 0 0 3px ${ACCENT}40, inset 0 0 0 1px ${ACCENT}` },
  "&.Mui-error.Mui-focused": { boxShadow: "0 0 0 3px #FF3B3040, inset 0 0 0 1px #FF3B30" },
};

const macButton = (primary: boolean) => ({
  textTransform: "none",
  fontFamily: FONT,
  fontSize: 13,
  fontWeight: 500,
  minWidth: 72,
  height: 26,
  px: 1.5,
  borderRadius: "6px",
  boxShadow: primary
    ? "0 0.5px 1px rgba(0,0,0,0.2), inset 0 0.5px 0 rgba(255,255,255,0.25)"
    : "0 0.5px 1px rgba(0,0,0,0.15), 0 0 0 0.5px rgba(0,0,0,0.12)",
  bgcolor: primary ? ACCENT : "background.paper",
  color: primary ? "#fff" : "text.primary",
  "&:hover": { bgcolor: primary ? "#0069E0" : "grey.50" },
  "&.Mui-disabled": {
    opacity: 0.45,
    color: primary ? "#fff" : "text.primary",
    bgcolor: primary ? ACCENT : "background.paper",
  },
});

function Group({ title, footer, children }: { title: string; footer?: React.ReactNode; children: React.ReactNode }) {
  return (
    <Box>
      <Typography sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 600, color: "text.primary", px: 1, mb: 0.75 }}>
        {title}
      </Typography>
      <Box
        sx={{
          bgcolor: "background.paper",
          borderRadius: "10px",
          boxShadow: "0 0 0 0.5px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.04)",
          overflow: "hidden",
        }}
      >
        {children}
      </Box>
      {footer && (
        <Typography sx={{ fontFamily: FONT, fontSize: 11, color: "text.secondary", px: 1, mt: 0.75 }}>
          {footer}
        </Typography>
      )}
    </Box>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        minHeight: 44,
        pl: 2,
        pr: 1.25,
        position: "relative",
        // inset separator, like macOS grouped lists
        "& + &::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 16,
          right: 0,
          borderTop: "0.5px solid var(--dk-border, rgba(0,0,0,0.1))",
        },
      }}
    >
      <Typography sx={{ fontFamily: FONT, fontSize: 13, color: "text.primary" }}>{label}</Typography>
      {children}
    </Box>
  );
}

export function ProjectContent({ id, setProjectData }: ProjectContentProps) {
  const setServerFiles = useSetRecoilState(serverFileState);
  const setProjectInfo = useSetRecoilState(projectInfoState);
  const setInspModelUuid = useSetRecoilState(inspModelUuidState);
  const setMyAIModule = useSetRecoilState(myAIModuleState);
  const [models, setModels] = useState<InspModelItem[]>([]);
  const [initial, setInitial] = useState<Form>(emptyForm);
  const [form, setForm] = useState<Form>(emptyForm);
  const [saving, setSaving] = useState(false);

  const applyProject = (p: IResponseProjectData) => {
    const next = {
      project_name: p.project_name ?? "",
      material_name: p.material_name ?? "",
      inspModel_id: p.inspModel_id,
    };
    setInitial(next);
    setForm(next);
    setProjectData(p);
  };

  useEffect(() => {
    (async () => {
      try {
        const [{ project }, modelList] = await Promise.all([getProject(Number(id)), getSimpleInspModelList()]);
        setServerFiles(imageFlat(project.images).map((image: { path: string }) => image.path));
        applyProject(project);
        setModels(modelList);
      } catch (error) {
        console.error(error);
      }
    })();
  }, [id]);

  const changed = (Object.keys(form) as (keyof Form)[]).filter((k) => form[k] !== initial[k]);
  const dirty = changed.length > 0;

  const handleSave = async () => {
    if (form.project_name.trim() === "") return toast.error(t("ProjectNameMissing"));
    const body: IEditProject = Object.fromEntries(changed.map((k) => [k, form[k]]));
    try {
      setSaving(true);
      const response = await editProject(Number(id), body);
      toast.success(t(response.message));
      applyProject(response.project);
      // ProjectLayout(ProjectHeader)은 리마운트되지 않으므로 공유 상태를 직접 갱신
      setProjectInfo((prev) => prev && { ...prev, ...body });
      if (changed.includes("inspModel_id")) {
        setInspModelUuid(form.inspModel_id ? (await getInspModel(form.inspModel_id)).uuid : null);
        // 검사모델이 바뀌면 이전 모델로 import된 모듈을 내리고 새 모델로 재import
        await removeImportedModules(response.project);
        setMyAIModule(null);
      }
    } catch (error: any) {
      console.error(error);
      toast.error(error?.message);
    } finally {
      setSaving(false);
    }
  };

  const selectedModel = models.find((m) => m.id === form.inspModel_id);

  return (
    <Box sx={{ maxWidth: 640, display: "flex", flexDirection: "column", gap: 3, fontFamily: FONT }}>
      <Group title={t("General")}>
        <Row label={t("ProjectName")}>
          <InputBase
            sx={fieldSx}
            value={form.project_name}
            error={form.project_name.trim() === ""}
            onChange={(e) => setForm({ ...form, project_name: e.target.value })}
          />
        </Row>
        <Row label={t("MaterialName")}>
          <InputBase
            sx={fieldSx}
            value={form.material_name}
            placeholder="—"
            onChange={(e) => setForm({ ...form, material_name: e.target.value })}
          />
        </Row>
      </Group>

      <Group title={t("Inspection")} footer={selectedModel?.description}>
        <Row label={t("InspModel")}>
          <Select<number | "">
            variant="standard"
            disableUnderline
            displayEmpty
            value={form.inspModel_id ?? ""}
            onChange={(e) => setForm({ ...form, inspModel_id: e.target.value === "" ? null : Number(e.target.value) })}
            IconComponent={() => (
              <UnfoldMoreIcon sx={{ fontSize: 14, color: "#fff", bgcolor: ACCENT, borderRadius: "4px", mr: 0.5 }} />
            )}
            renderValue={(v) => (v === "" ? t("NoModelMsg") : (selectedModel?.name ?? ""))}
            MenuProps={{
              PaperProps: {
                sx: {
                  mt: 0.5,
                  borderRadius: "8px",
                  boxShadow: "0 0 0 0.5px rgba(0,0,0,0.15), 0 8px 24px rgba(0,0,0,0.15)",
                  "& .MuiMenuItem-root": {
                    fontFamily: FONT,
                    fontSize: 13,
                    mx: 0.5,
                    borderRadius: "4px",
                    minHeight: 24,
                    "&.Mui-selected, &:hover, &.Mui-focusVisible": { bgcolor: ACCENT, color: "#fff" },
                  },
                },
              },
            }}
            sx={{
              minWidth: 180,
              fontFamily: FONT,
              fontSize: 13,
              height: 24,
              pl: 1,
              borderRadius: "6px",
              bgcolor: "background.paper",
              boxShadow: "0 0.5px 1px rgba(0,0,0,0.15), 0 0 0 0.5px rgba(0,0,0,0.12)",
              "& .MuiSelect-select": { py: 0, pr: "28px !important", bgcolor: "transparent !important" },
            }}
          >
            {models.map((m) => (
              <MenuItem key={m.id} value={m.id}>
                {m.name}
              </MenuItem>
            ))}
          </Select>
        </Row>
      </Group>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
        <Button disableRipple sx={macButton(false)} disabled={!dirty || saving} onClick={() => setForm(initial)}>
          {t("Cancel")}
        </Button>
        <Button disableRipple sx={macButton(true)} disabled={!dirty || saving} onClick={handleSave}>
          {t("Save")}
        </Button>
      </Box>
    </Box>
  );
}
